import { useQueries, useQueryClient } from "@tanstack/react-query";
import { SofaFeed, NormalizedRelease } from "@/types/sofa";
import { useEffect } from "react";
import { recordSofaAnomaly } from "@/utils/sofaDiagnostics";
import { validateRelease } from "@/utils/sofaSchema";

const FEED_URLS = [
  { key: "sofa-macos", url: "https://sofa.macadmins.io/v2/macos_data_feed.json" },
  { key: "sofa-ios", url: "https://sofa.macadmins.io/v2/ios_data_feed.json" },
  { key: "sofa-safari", url: "https://sofa.macadmins.io/v2/safari_data_feed.json" },
  { key: "sofa-tvos", url: "https://sofa.macadmins.io/v2/tvos_data_feed.json" },
  { key: "sofa-watchos", url: "https://sofa.macadmins.io/v2/watchos_data_feed.json" },
  { key: "sofa-visionos", url: "https://sofa.macadmins.io/v2/visionos_data_feed.json" },
];

export { FEED_URLS };

const CACHE_PREFIX = "sofa-cache-";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

function readCache(key: string): SofaFeed | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return data as SofaFeed;
  } catch {
    return null;
  }
}

function writeCache(key: string, data: SofaFeed) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    // Storage full — ignore
  }
}

function isUsableFeed(data: any): boolean {
  // Accept both OS-style feeds (macOS/iOS/tvOS/etc) and AppVersions-style feeds (Safari)
  return !!(data && (Array.isArray(data.OSVersions) || Array.isArray(data.AppVersions)));
}

async function fetchFeed(url: string, key: string): Promise<SofaFeed> {
  // Try direct fetch first
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (res.ok) {
      const data = await res.json();
      if (isUsableFeed(data)) {
        writeCache(key, data);
        return data;
      }
    }
  } catch {
    // CORS blocked or timeout — fall through to proxy
  }

  // Try codetabs first — empirically the most reliable proxy
  try {
    const proxyUrl = `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(15000) });
    if (res.ok) {
      const data = await res.json();
      if (isUsableFeed(data)) {
        writeCache(key, data);
        return data as SofaFeed;
      }
    }
  } catch {
    // Fall through
  }

  // Fallback: allorigins /get endpoint
  try {
    const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
    const res = await fetch(proxyUrl, { signal: AbortSignal.timeout(15000) });
    if (res.ok) {
      const wrapper = await res.json();
      if (wrapper?.contents) {
        const data = JSON.parse(wrapper.contents) as SofaFeed;
        if (isUsableFeed(data)) {
          writeCache(key, data);
          return data;
        }
      }
    }
  } catch {
    // Fall through
  }

  throw new Error(`All fetch attempts failed for ${url}`);
}

function resolveUpdateSummary(release: any): { priority: string; summary: string; recommendation: string; stats: any } {
  // Handle both lowercase (typed) and uppercase (actual API) field names
  const raw = release.update_summary || release.UpdateSummary;
  if (!raw) {
    return {
      priority: "unknown",
      summary: `${release.UniqueCVEsCount || 0} CVEs addressed`,
      recommendation: "Review and update",
      stats: { exploited: 0, critical: 0, high: 0, medium: 0, low: 0, remote: 0, total: release.UniqueCVEsCount || 0 },
    };
  }
  return {
    priority: raw.priority || raw.Priority || "unknown",
    summary: raw.summary || raw.Summary || "",
    recommendation: raw.recommendation || raw.Recommendation || "",
    stats: {
      exploited: raw.stats?.exploited ?? raw.Stats?.Exploited ?? 0,
      critical: raw.stats?.critical ?? raw.Stats?.Critical ?? 0,
      high: raw.stats?.high ?? raw.Stats?.High ?? 0,
      medium: raw.stats?.medium ?? raw.Stats?.Medium ?? 0,
      low: raw.stats?.low ?? raw.Stats?.Low ?? 0,
      remote: raw.stats?.remote ?? raw.Stats?.Remote ?? 0,
      total: raw.stats?.total ?? raw.Stats?.Total ?? 0,
    },
  };
}

function normalizeReleases(feed: SofaFeed): NormalizedRelease[] {
  const releases: NormalizedRelease[] = [];
  if (!feed) return releases;

  // Some feeds (Safari) use AppVersions instead of OSVersions; both have SecurityReleases under entries.
  const versionGroups: any[] = [
    ...(Array.isArray((feed as any).OSVersions) ? (feed as any).OSVersions : []),
    ...(Array.isArray((feed as any).AppVersions) ? (feed as any).AppVersions : []),
  ];

  for (const group of versionGroups) {
    const securityReleases = group?.SecurityReleases;
    if (!Array.isArray(securityReleases)) continue;

    for (const rawRelease of securityReleases) {
      // Schema-validate before any downstream filter sees the release.
      // Guarantees ProductName / ProductVersion are non-empty strings.
      const release = validateRelease(rawRelease, "normalizeReleases");
      if (!release) continue;

      const cves = Object.entries(release.CVEs || {}).map(([id, detail]) => ({ id, detail }));
      const summary = resolveUpdateSummary(release);
      releases.push({
        id: `${release.ProductName}-${release.ProductVersion}-${release.Build || ""}`,
        updateName: release.UpdateName,
        productName: release.ProductName,
        productVersion: release.ProductVersion,
        releaseDate: release.ReleaseDate,
        cveCount: release.UniqueCVEsCount,
        activelyExploitedCount: (release.ActivelyExploitedCVEs || []).length,
        activelyExploitedCVEs: release.ActivelyExploitedCVEs || [],
        priority: summary.priority,
        summary: summary.summary,
        recommendation: summary.recommendation,
        stats: summary.stats,
        securityInfo: release.SecurityInfo,
        supportedDevices: release.SupportedDevices || [],
        deviceScope: release.DeviceScope || "universal",
        cves,
        osVersion: group.OSVersion || group.AppVersion || "",
      });
    }
  }

  return releases;
}

/** Prefetch all SOFA feeds — call once on app mount to warm the cache */
export function usePrefetchSofaFeeds() {
  const queryClient = useQueryClient();

  useEffect(() => {
    FEED_URLS.forEach(({ key, url }) => {
      // Seed from localStorage instantly
      const cached = readCache(key);
      if (cached) {
        queryClient.setQueryData([key], cached);
      }
      // Background fetch
      queryClient.prefetchQuery({
        queryKey: [key],
        queryFn: () => fetchFeed(url, key),
        staleTime: 60 * 60 * 1000,
      });
    });
  }, [queryClient]);
}

export function useSofaFeed() {
  const feedQueries = useQueries({
    queries: FEED_URLS.map(({ key, url }) => ({
      queryKey: [key],
      queryFn: () => fetchFeed(url, key),
      staleTime: 30 * 60 * 1000, // 30 minutes
      gcTime: 2 * 60 * 60 * 1000, // 2 hours
      refetchInterval: 30 * 60 * 1000, // Auto-refresh every 30 minutes
      refetchIntervalInBackground: true,
      retry: 1,
      retryDelay: 2000,
      // Seed initialData from localStorage for instant display
      initialData: () => readCache(key) ?? undefined,
      initialDataUpdatedAt: () => {
        try {
          const raw = localStorage.getItem(CACHE_PREFIX + key);
          if (raw) return JSON.parse(raw).ts;
        } catch {}
        return undefined;
      },
    })),
  });

  const isRefetching = feedQueries.some(q => q.isRefetching);
  const refetch = () => feedQueries.forEach(q => q.refetch());
  // Only error if ALL feeds failed
  const error = feedQueries.every(q => q.error) ? feedQueries[0].error : null;

  // Progressive: count how many have loaded
  const loadedCount = feedQueries.filter(q => q.data).length;
  const totalCount = feedQueries.length;
  // Consider "loading" only if ZERO feeds have data
  const isLoading = loadedCount === 0 && feedQueries.some(q => q.isLoading);

  // Cache status: "fresh" if all loaded queries were fetched (not from initialData),
  // "cached" if any are still showing stale/initial data, "fetching" if actively refreshing
  const isFresh = feedQueries.every(q => q.isFetched && !q.isStale);
  const cacheStatus: "fresh" | "cached" | "fetching" = isRefetching
    ? "fetching"
    : isFresh
      ? "fresh"
      : "cached";

  // Determine the most recent dataUpdatedAt across all queries
  const lastDataUpdate = Math.max(...feedQueries.map(q => q.dataUpdatedAt || 0));

  const allReleases: NormalizedRelease[] = [];
  for (const q of feedQueries) {
    if (q.data) allReleases.push(...normalizeReleases(q.data));
  }

  allReleases.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());

  const seen = new Set<string>();
  const releases = allReleases.filter(r => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });

  const allCVEMap = new Map<string, { id: string; detail: any; releases: string[] }>();
  for (const release of releases) {
    for (const cve of release.cves) {
      if (!allCVEMap.has(cve.id)) {
        allCVEMap.set(cve.id, { ...cve, releases: [] });
      }
      allCVEMap.get(cve.id)!.releases.push(release.updateName);
    }
  }

  const totalCVEs = allCVEMap.size;
  const activelyExploitedCVEs = new Set<string>();
  for (const release of releases) {
    release.activelyExploitedCVEs.forEach(c => activelyExploitedCVEs.add(c));
  }

  const lastCheck = feedQueries.find(q => q.data?.LastCheck)?.data?.LastCheck;

  return {
    releases,
    totalCVEs,
    activelyExploitedCount: activelyExploitedCVEs.size,
    isLoading,
    isRefetching,
    refetch,
    error,
    lastCheck,
    loadedCount,
    totalCount,
    cacheStatus,
    lastDataUpdate,
  };
}
